import React from 'react';
import { Helmet } from 'react-helmet-async';

const getOrigin = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  // Fallback for SSR - will be replaced with actual domain on client-side
  return typeof window !== 'undefined' ? window.location.origin : '';
};

export const BlogListSEO = () => {
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    'name': 'Gym Rat Rehab Physical Therapy Blog',
    'description': 'Expert insights on physical therapy, sports injury recovery, orthopedic rehabilitation, manual therapy techniques, and vertigo treatment by Douglas Mitchell, PT, DPT in Gilbert, Arizona.',
    'url': `${getOrigin()}/blog`,
    'publisher': {
      '@type': 'Organization',
      'name': 'Gym Rat Rehab LLC',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg'
      }
    },
    'author': {
      '@type': 'Person',
      'name': 'Douglas Mitchell',
      'jobTitle': 'Physical Therapist, DPT',
      'description': 'Licensed Physical Therapist with 20+ years experience in Gilbert, Arizona'
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': `${getOrigin()}/`
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': 'Blog',
        'item': `${getOrigin()}/blog`
      }
    ]
  };

  return (
    <Helmet>
      <title>Physical Therapy Blog Gilbert AZ | Expert PT Tips & Insights | Gym Rat Rehab</title>
      <meta name="description" content="Expert physical therapy blog by Douglas Mitchell, PT, DPT. Topics: sports injury recovery, orthopedic rehab, manual therapy, dry needling, vertigo treatment. Gilbert, Arizona insights." />
      <meta name="keywords" content="physical therapy blog, sports injury recovery tips, orthopedic rehabilitation, manual therapy techniques, dry needling information, vertigo treatment, gilbert arizona physical therapy" />
      <link rel="canonical" href={`${getOrigin()}/blog`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Physical Therapy Blog Gilbert AZ | Expert PT Tips & Insights" />
      <meta property="og:description" content="Expert physical therapy insights by Douglas Mitchell, PT, DPT. Sports injury recovery, orthopedic rehab, manual therapy, and vertigo treatment." />
      <meta property="og:url" content={`${getOrigin()}/blog`} />
      <meta property="og:image" content="https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Physical Therapy Blog Gilbert AZ | Expert PT Tips & Insights" />
      <meta name="twitter:description" content="Expert physical therapy insights by Douglas Mitchell, PT, DPT. Sports injury recovery, orthopedic rehab, manual therapy, and vertigo treatment." />
      <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg" />
      <link rel="alternate" type="application/rss+xml" title="GymRatRehab Blog RSS" href={`${process.env.REACT_APP_BACKEND_URL}/api/blog/rss`} />
      
      {/* Blog Schema */}
      <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      
      {/* Breadcrumb Schema */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </Helmet>
  );
};

export const BlogArticleSEO = ({ article }) => {
  const canonical = `${getOrigin()}/blog/${article.slug}`;
  const faqSchema = (article.faqs && article.faqs.length > 0) ? ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': article.faqs.map(f => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a }
    }))
  }) : null;
  return (
    <Helmet>
      <title>{article.metaTitle || article.title}</title>
      <meta name="description" content={article.metaDescription || article.excerpt} />
      <meta name="keywords" content={(article.tags || []).join(', ')} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={article.metaTitle || article.title} />
      <meta property="og:description" content={article.metaDescription || article.excerpt} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={article.image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.metaTitle || article.title} />
      <meta name="twitter:description" content={article.metaDescription || article.excerpt} />
      <meta name="twitter:image" content={article.image} />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': article.title,
        'description': article.excerpt,
        'image': article.image ? [article.image] : undefined,
        'author': {
          '@type': 'Person',
          'name': article.author
        },
        'datePublished': article.date,
        'dateModified': article.date,
        'mainEntityOfPage': {
          '@type': 'WebPage',
          '@id': canonical
        }
      })}</script>
      {faqSchema && (<script type="application/ld+json">{JSON.stringify(faqSchema)}</script>)}
    </Helmet>
  );
};

// Page-level SEO components for core pages
const pageSEO = (path, { title, description }) => (
  <Helmet>
    <title>{title}</title>
    <meta name="description" content={description} />
    <link rel="canonical" href={`${getOrigin()}${path}`} />
    <meta property="og:type" content="website" />
    <meta property="og:title" content={title} />
    <meta property="og:description" content={description} />
    <meta property="og:url" content={`${getOrigin()}${path}`} />
    <meta property="og:image" content="https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg" />
  </Helmet>
);

export const ServicesSEO = () => pageSEO('/services', {
  title: 'Physical Therapy Services Gilbert AZ | Sports Injury, Orthopedic, Vertigo Care',
  description: 'Comprehensive PT services in Gilbert, AZ: Sports injury recovery, orthopedic rehabilitation, manual therapy, dry needling, and vertigo treatment. Cash-based care with Douglas Mitchell, PT, DPT. Call (480) 757-9713'
});

export const AboutSEO = () => pageSEO('/about', {
  title: 'About Douglas Mitchell, PT, DPT | Physical Therapist Gilbert Arizona',
  description: 'Douglas Mitchell, PT, DPT—24+ years experience in sports injury recovery and orthopedic rehabilitation. Expert physical therapist serving Gilbert, Mesa, Chandler, and Queen Creek, AZ.'
});

export const ContactSEO = () => pageSEO('/contact', {
  title: 'Contact Physical Therapist Gilbert AZ | (480) 757-9713 | Gym Rat Rehab',
  description: 'Schedule your physical therapy consultation in Gilbert, AZ. Call (480) 757-9713. Open Tues–Thurs 9AM–5PM. Located inside Alexander Medical Center. Serving Southeast Valley Arizona.'
});

export const PricingSEO = () => pageSEO('/pricing', {
  title: 'Pricing | Cash-Based Physical Therapy | GymRatRehab',
  description: 'Transparent cash-based pricing for expert physical therapy. Fewer, longer 1:1 sessions focused on outcomes—with package discounts available.'
});

export const FAQSEO = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What conditions can Physical Therapy help with?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Physical therapy can effectively treat sports injuries (ACL/MCL tears, ankle sprains), orthopedic conditions (arthritis, joint replacements), musculoskeletal pain (back pain, neck pain), post-surgical rehabilitation, movement disorders, balance issues, chronic pain, work-related injuries, and auto accident injuries in Gilbert, Arizona."
        }
      },
      {
        "@type": "Question",
        "name": "Where is GymRatRehab located in Gilbert, Arizona?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "GymRatRehab is located inside Alexander Medical Center at 2680 S Val Vista Dr Ste.131, Bldg. 6, Gilbert, AZ 85295. We serve Apache Junction, Chandler, Gilbert, Mesa, Queen Creek, and San Tan Valley."
        }
      },
      {
        "@type": "Question",
        "name": "What are the office hours for GymRatRehab?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "GymRatRehab is available by appointment only on Tuesdays and Thursdays from 2:00 PM to 5:00 PM. Call (480) 757-9713 or email Info@gymratrehab.com to schedule."
        }
      },
      {
        "@type": "Question",
        "name": "How experienced is Douglas Mitchell, PT, DPT?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Douglas Mitchell, PT, DPT is a licensed Doctor of Physical Therapy with over 20 years of clinical experience. He has successfully treated over 1000+ patients in Gilbert, Arizona, specializing in sports injuries and orthopedic rehabilitation."
        }
      },
      {
        "@type": "Question",
        "name": "Do you treat ACL injuries and post-surgical rehabilitation?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, Douglas Mitchell, PT, DPT specializes in ACL injury recovery and post-surgical rehabilitation. Treatment includes movement analysis, sport-specific training, and return-to-sport protocols."
        }
      },
      {
        "@type": "Question",
        "name": "What is dry needling and do you offer it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Dry needling is an advanced manual therapy technique using thin needles to treat trigger points and muscle dysfunction. Douglas Mitchell, PT, DPT offers this specialized technique as part of comprehensive treatment plans in Gilbert, AZ."
        }
      }
    ]
  };

  return (
    <Helmet>
      <title>Physical Therapy FAQ Gilbert AZ | Common Questions | Gym Rat Rehab</title>
      <meta name="description" content="Common questions about physical therapy in Gilbert, AZ. Learn about our services, hours, insurance-free pricing, sports injury treatment, and vestibular care. Call (480) 757-9713" />
      <link rel="canonical" href={`${getOrigin()}/faq`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Physical Therapy FAQ Gilbert AZ | Common Questions | Gym Rat Rehab" />
      <meta property="og:description" content="Common questions about physical therapy in Gilbert, AZ. Services, hours, insurance-free pricing, sports injury treatment, and vestibular care." />
      <meta property="og:url" content={`${getOrigin()}/faq`} />
      <meta property="og:image" content="https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Physical Therapy FAQ Gilbert AZ | Common Questions | Gym Rat Rehab" />
      <meta name="twitter:description" content="Common questions about physical therapy in Gilbert, AZ. Services, hours, insurance-free pricing, sports injury treatment, and vestibular care." />
      <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg" />
      
      {/* FAQPage Schema */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
};

export const FavoritesSEO = () => pageSEO('/favorite-places', {
  title: 'My Favorite Places | GymRatRehab',
  description: 'Trusted places and partners in Arizona, including Alexander Medical Center. Curated by Douglas Mitchell, DPT.'
});

export const HipaaSEO = () => pageSEO('/hipaa-notice', {
  title: 'HIPAA Notice of Privacy Practices | GymRatRehab',
  description: 'Your health information privacy rights and how GymRatRehab safeguards your data under HIPAA.'
});

export const PrivacySEO = () => pageSEO('/privacy-policy', {
  title: 'Privacy Policy | GymRatRehab',
  description: 'How GymRatRehab collects, uses, and protects your information. Contact us with any privacy questions.'
});

export const TermsSEO = () => pageSEO('/terms-of-service', {
  title: 'Terms of Service | GymRatRehab',
  description: 'Terms and conditions for using GymRatRehab services and website.'
});
